import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { Card, CardTitle, CardBody } from '../components/ui/Card';
import { Pagination } from '../components/ui/Pagination';
import { Loading } from '../components/ui/Loading';
import { ToggleLeft, Search, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

// Human-readable metadata for known feature flags
const FLAG_META: Record<string, { label: string; description: string }> = {
  external_agent_firewall: {
    label: 'External Agent Firewall',
    description: 'Enable firewall protection for external AI agents',
  },
};

const FeatureFlags: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const pageSize = 15;
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedAccountName, setSelectedAccountName] = useState<string>('');
  const queryClient = useQueryClient();

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts', page, pageSize],
    queryFn: () => adminApiClient.getAccounts({ page, page_size: pageSize }),
  });

  const { data: featuresData, isLoading: featuresLoading } = useQuery({
    queryKey: ['account-features', selectedAccountId],
    queryFn: () => adminApiClient.getAccountFeatures(selectedAccountId!),
    enabled: !!selectedAccountId,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ feature, enabled }: { feature: string; enabled: boolean }) =>
      adminApiClient.setAccountFeature(selectedAccountId!, feature, enabled),
    onSuccess: (data) => {
      toast.success(`${data.feature} ${data.enabled ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries({ queryKey: ['account-features', selectedAccountId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update feature flag');
    },
  });

  const filteredAccounts = accounts?.data?.filter((account) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      account.name?.toLowerCase().includes(term) ||
      account.id.toLowerCase().includes(term) ||
      account.clerk_org_id?.toLowerCase().includes(term)
    );
  });

  const handleSelectAccount = (accountId: string, accountName: string) => {
    setSelectedAccountId(accountId);
    setSelectedAccountName(accountName || accountId);
  };

  // All known flags — backend GetAll returns all KnownFlags with status
  const allFlags = featuresData?.features
    ? Object.entries(featuresData.features)
    : [];

  const enabledCount = allFlags.filter(([, enabled]) => enabled).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <ToggleLeft className="w-6 h-6 text-primary-400" />
        <h2 className="text-2xl font-bold text-white">Feature Flags</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account list */}
        <div className="lg:col-span-1">
          <Card padding="none">
            <div className="p-4 border-b border-gray-700">
              <CardTitle>Select Organization</CardTitle>
              <div className="mt-3 relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            {accountsLoading ? (
              <Loading />
            ) : (
              <>
                <div className="max-h-[600px] overflow-y-auto">
                  {filteredAccounts?.map((account) => (
                    <button
                      key={account.id}
                      onClick={() => handleSelectAccount(account.id, account.name)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-700/50 transition-colors ${
                        selectedAccountId === account.id
                          ? 'bg-primary-500/10 border-l-2 border-l-primary-500'
                          : 'hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {account.name || 'Unnamed'}
                          </p>
                          <p className="text-gray-500 text-xs font-mono truncate mt-0.5">
                            {account.clerk_org_id || account.id}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                  {filteredAccounts?.length === 0 && (
                    <div className="p-8 text-center text-gray-500 text-sm">
                      No accounts match your search
                    </div>
                  )}
                </div>
                {!searchTerm && accounts && accounts.total_pages > 1 && (
                  <div className="p-3 border-t border-gray-700">
                    <Pagination
                      currentPage={page}
                      totalPages={accounts.total_pages}
                      totalItems={accounts.total}
                      pageSize={pageSize}
                      onPageChange={setPage}
                    />
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        {/* Flags panel */}
        <div className="lg:col-span-2">
          <Card>
            <div className="pb-4 border-b border-gray-700 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>
                    {selectedAccountId
                      ? `Flags — ${selectedAccountName}`
                      : 'Feature Flags'}
                  </CardTitle>
                  {selectedAccountId && allFlags.length > 0 && (
                    <p className="text-gray-500 text-sm mt-1">
                      {enabledCount} of {allFlags.length} flags enabled
                    </p>
                  )}
                </div>
                {selectedAccountId && (
                  <span className="text-xs text-gray-500 font-mono">
                    {selectedAccountId}
                  </span>
                )}
              </div>
            </div>
            <CardBody>
              {!selectedAccountId ? (
                <div className="py-12 text-center text-gray-500">
                  <ToggleLeft className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-lg font-medium">No organization selected</p>
                  <p className="text-sm mt-1">
                    Select an organization from the list to manage its feature flags
                  </p>
                </div>
              ) : featuresLoading ? (
                <div className="py-12 text-center">
                  <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto" />
                </div>
              ) : allFlags.length > 0 ? (
                <div className="space-y-3">
                  {allFlags.map(([flag, enabled]) => {
                    const meta = FLAG_META[flag];
                    return (
                      <div
                        key={flag}
                        className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                          enabled
                            ? 'bg-primary-500/5 border-primary-500/30'
                            : 'bg-gray-800/50 border-gray-700'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          {enabled ? (
                            <CheckCircle2 className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                          )}
                          <div>
                            <p className="text-white font-medium text-sm">
                              {meta?.label || flag}
                            </p>
                            <p className="text-gray-500 text-xs mt-0.5">
                              {meta?.description || flag}
                            </p>
                            <p className="text-gray-600 text-xs font-mono mt-1">
                              {flag}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            toggleMutation.mutate({
                              feature: flag,
                              enabled: !enabled,
                            })
                          }
                          disabled={toggleMutation.isPending}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                            enabled ? 'bg-primary-500' : 'bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-12 text-center text-gray-400">
                  No feature flags available
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FeatureFlags;
